/**
 * 工时记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WorkTimeService } from '../../../services/workTimeService';
import { formatMoney } from '../../../utils/budgetUtils';

interface WorkTimePanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface WorkTimeRecord {
  _id: string;
  workDate: string;
  workerType: string;
  workerName?: string;
  maleWorkerCount: number;
  femaleWorkerCount: number;
  workUnits?: number;
  timeUnit?: string;
  price: number;
  totalCost?: number;
  workContent?: string;
}

const WorkTimePanel: React.FC<WorkTimePanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkTimeRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await WorkTimeService.getWorkTimes({
        projectId,
        page,
        limit: pageSize
      });
      
      if (response.success && response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('获取工时记录失败:', error);
      message.error('获取工时记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  // 首次激活时加载数据
  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<WorkTimeRecord> = isMobile ? [
    {
      title: '日期/工种',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{formatDate(record.workDate)}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.workerType === 'male' ? '男工' : '女工'} · 
            {record.workerName || '-'} · 
            {record.workUnits || (record.timeUnit === 'half' ? 0.5 : 1)}工
          </div>
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 80,
      align: 'right',
      render: (val: number) => val ? formatMoney(val, false) : formatMoney(0, false)
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'workDate',
      key: 'workDate',
      width: 100,
      render: (val: string) => formatDate(val)
    },
    {
      title: '工种',
      dataIndex: 'workerType',
      key: 'workerType',
      width: 70,
      render: (val: string) => val === 'male' ? '男工' : '女工'
    },
    {
      title: '姓名',
      dataIndex: 'workerName',
      key: 'workerName',
      width: 90,
      render: (val) => val || '-'
    },
    {
      title: '工时',
      key: 'workUnits',
      width: 70,
      render: (_, record) => {
        const units = record.workUnits || (record.timeUnit === 'half' ? 0.5 : 1);
        return `${units}工`;
      }
    },
    {
      title: '金额',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 100,
      align: 'right',
      render: (val: number) => val ? formatMoney(val, false) : formatMoney(0, false)
    },
    {
      title: '备注',
      dataIndex: 'workContent',
      key: 'workContent',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return <Empty description="暂无工时记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="_id"
      loading={loading}
      size="small"
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
        onChange: (page, pageSize) => fetchData(page, pageSize)
      }}
    />
  );
};

export default WorkTimePanel;
